import { colors } from '@mm/utils';
import classNames from 'classnames';
import React, { ChangeEvent, FocusEvent, KeyboardEvent, useState } from 'react';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { Spinner } from '../Spinner/Spinner';
import styles from './Input.module.css';

type Props = {
  css?: {
    container?: string;
    input?: string;
    icon?: string;
  };
  value?: string;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSearch?: (value: string) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
  invalid?: boolean;
  icon?: React.ReactNode;
  type?: 'text' | 'search' | 'number';
  readOnly?: boolean;
  testId?: string;
};

export const Input = React.forwardRef(
  (
    {
      css = {},
      disabled,
      placeholder,
      maxLength,
      value = '',
      autoFocus,
      onSearch,
      onFocus = () => {},
      onBlur = () => {},
      onKeyDown = () => {},
      onChange,
      isLoading,
      icon,
      invalid,
      type = 'search',
      readOnly,
      testId,
    }: Props,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleBlur = e => {
      setIsFocused(false);
      onBlur(e);
    };
    const handleFocus = e => {
      setIsFocused(true);
      onFocus(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (type === 'search' && e.key === ENTER_KEY_NAME) {
        const target = e.target as HTMLInputElement;

        if (onSearch) {
          onSearch(target.value);
        }

        if (onKeyDown) {
          onKeyDown(e);
        }

        e.preventDefault();
      }
    };

    return (
      <div
        className={classNames(
          styles.container,
          {
            [styles.container__disabled]: disabled,
            [styles.container__focused]: isFocused,
            [styles.container__invalid]: invalid,
          },
          css?.container,
        )}
      >
        <input
          ref={ref}
          maxLength={maxLength}
          className={classNames(styles.input, css?.input)}
          type={type}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          readOnly={readOnly}
          data-test={testId}
        />

        {(icon || isLoading) && (
          <div className={classNames(styles.iconContainer, css?.icon)}>
            {isLoading && (
              <Spinner
                show
                size={14}
                position="relative"
                color={colors.darkGray}
                backgroundColor="transparent"
              />
            )}

            {!isLoading && icon}
          </div>
        )}
      </div>
    );
  },
);
